import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../api/commonAPI';
import { OrderResponse } from '../../types';
import OrderHistorySidebar from '../../pages/order/OrderHistorySidebar';
import { useWebSocket } from '../../components/WebSocketContext';
import TableInfoDialog from './TableInfoDialog';
import useOrderAlarm from '../../hooks/useOrderAlarm';
import './TableListPage.css';

interface Table {
    id: number;
    tableName: string;
    nfcUrl: string;
    isActive: boolean;
}


type TableListPageParams = {
    storeId: string;
};

const mergeOrdersById = (baseOrders: OrderResponse[], incomingOrders: OrderResponse[]) => {
    const merged = new Map<number, OrderResponse>();

    baseOrders.forEach(order => merged.set(order.orderId, order));
    incomingOrders.forEach(order => merged.set(order.orderId, order));

    return Array.from(merged.values()).sort((a, b) => b.orderId - a.orderId);
};

const TableListPage = () => {
    const navigate = useNavigate();
    const { storeId } = useParams<TableListPageParams>();
    const { orders: liveOrders, staffCalls } = useWebSocket();

    const notifiedOrderIds = useRef<Set<number>>(new Set());
    const notifiedStaffCalls = useRef<Set<string>>(new Set());
    const appliedLiveOrders = useRef(new Map<number, OrderResponse>());
    const liveOrdersRef = useRef(liveOrders);
    liveOrdersRef.current = liveOrders;

    const [tables, setTables] = useState<Table[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [orders, setOrders] = useState<{ [key: number]: OrderResponse[] }>({});
    const [loadingOrders, setLoadingOrders] = useState<{ [key: number]: boolean }>({});
    const [orderErrors, setOrderErrors] = useState<{ [key: number]: string | null }>({});
    const [popup, setPopup] = useState<{ visible: boolean; message: string } | null>(null);
    const [showTableInfo, setShowTableInfo] = useState(false);
    const [selectedReceiptTable, setSelectedReceiptTable] = useState<Table | null>(null);
    const [pendingTables, setPendingTables] = useState<Record<number, boolean>>({});
    const pendingTableIds = useRef(new Set<number>());
    const hasUnconfirmedOrders = !isLoading && !error && tables.some(table =>
        (orders[table.id] || []).some(order => order.status === 'NEW')
    );
    const { audioState, resumeAudio, playNotificationSound } = useOrderAlarm(hasUnconfirmedOrders);

    useEffect(() => {
        if (!storeId) return;
        let active = true;
        // Cached WebSocket messages may predate the HTTP snapshot. Only apply
        // newly received messages while loading, then retain them in the merge.
        appliedLiveOrders.current = new Map(
            Object.values(liveOrdersRef.current).flat().map(order => [order.orderId, order])
        );
        setTables([]);
        setOrders({});
        setError(null);
        notifiedOrderIds.current.clear();

        const fetchTablesAndOrders = async () => {
            try {
                setIsLoading(true);
                const res = await axiosInstance.get<Table[]>(`/api/stores/${storeId}/admin/tables`);
                if (!active) return;
                setTables(res.data);
                setError(null);

                const loadingOrdersInit: { [key: number]: boolean } = {};
                const orderErrorsInit: { [key: number]: string | null } = {};
                const ordersInit: { [key: number]: OrderResponse[] } = {};

                res.data.forEach(table => {
                    loadingOrdersInit[table.id] = true;
                    orderErrorsInit[table.id] = null;
                    ordersInit[table.id] = [];
                });
                setLoadingOrders(loadingOrdersInit);
                setOrderErrors(orderErrorsInit);

                const orderPromises = res.data.map(async (table) => {
                    try {
                        const orderRes = await axiosInstance.get<OrderResponse[]>(`/api/stores/${storeId}/admin/tables/${table.id}/orders`);
                        if (!active) return;
                        ordersInit[table.id] = orderRes.data;
                        orderRes.data.forEach(order => notifiedOrderIds.current.add(order.orderId));
                    } catch {
                        if (active) setOrderErrors(prev => ({ ...prev, [table.id]: '주문 내역을 불러오지 못했습니다.' }));
                    } finally {
                        if (active) setLoadingOrders(prev => ({ ...prev, [table.id]: false }));
                    }
                });

                await Promise.all(orderPromises);
                if (!active) return;
                setOrders(currentOrders => Object.fromEntries(res.data.map(table => [
                    table.id,
                    mergeOrdersById(ordersInit[table.id], currentOrders[table.id] || []),
                ])));
            } catch {
                if (active) setError('테이블 목록을 불러오지 못했습니다.');
            } finally {
                if (active) setIsLoading(false);
            }
        };

        fetchTablesAndOrders();
        return () => { active = false; };
    }, [storeId]);



    useEffect(() => {
        if (!liveOrders || !storeId) return;

        Object.entries(liveOrders).forEach(([tableId, wsOrders]) => {
            if (!Array.isArray(wsOrders) || wsOrders.length === 0) return;
            const changedOrders = wsOrders.filter(order =>
                (order.storeId == null || order.storeId === Number(storeId)) &&
                appliedLiveOrders.current.get(order.orderId) !== order
            );
            if (changedOrders.length === 0) return;
            changedOrders.forEach(order => appliedLiveOrders.current.set(order.orderId, order));
            setOrders(prev => ({
                ...prev,
                [Number(tableId)]: mergeOrdersById(prev[Number(tableId)] || [], changedOrders)
            }));
        });
    }, [liveOrders, storeId]);

    useEffect(() => {
        tables.forEach(table => {
            const orderList = liveOrders[table.id] || [];
            const newOrder = orderList.find(order => order.status === 'NEW');

            if (newOrder && !notifiedOrderIds.current.has(newOrder.orderId)) {
                notifiedOrderIds.current.add(newOrder.orderId);
                setPopup({ visible: true, message: `${table.tableName}에 새 주문이 들어왔습니다.` });

                setTimeout(() => setPopup(null), 3000);
            }
        });
    }, [tables, liveOrders]);

    useEffect(() => {
        if (!storeId) return;

        staffCalls
            .filter(staffCall => staffCall.storeId === Number(storeId))
            .forEach(staffCall => {
                const callKey = `${staffCall.tableId}-${staffCall.createdAt}`;
                if (notifiedStaffCalls.current.has(callKey)) return;

                notifiedStaffCalls.current.add(callKey);
                playNotificationSound();
                setPopup({ visible: true, message: `${staffCall.tableName}에서 직원을 호출했습니다.` });

                setTimeout(() => setPopup(null), 3500);
            });
    }, [staffCalls, storeId, playNotificationSound]);

    const handleGoBack = () => navigate('/');
    const handleGoToCreatePage = () => navigate(`/admin/stores/${storeId}/tables/new`);
    const handleGoToQrPrintPage = () => navigate(`/admin/stores/${storeId}/tables/qr-print`);
    const getActiveOrdersForTable = useCallback((tableId: number) => {
        return (orders[tableId] || [])
            .filter(order => order.status !== 'COMPLETED' && order.status !== 'CANCELED');
    }, [orders]);

    const handleUpdateOrder = async (orderId: number, tableId: number, status: 'PROCESSING' | 'SERVED') => {
        try {
            await axiosInstance.patch(`/api/stores/${storeId}/admin/orders/${orderId}/status`, {
                status,
            });
            setOrders(prevOrders => {
                const tableOrders = prevOrders[tableId] || [];
                const updated = tableOrders.map(order =>
                    order.orderId === orderId ? { ...order, status } : order
                );
                return { ...prevOrders, [tableId]: updated };
            });
        } catch {
            alert('주문 상태를 변경하지 못했습니다.');
        }
    };

    const handleCancelOrderItem = async (orderId: number, orderItemId: number, tableId: number) => {
        if (!storeId) return;
        if (!window.confirm('이 주문 항목만 취소할까요?')) return;

        try {
            const response = await axiosInstance.delete<OrderResponse>(
                `/api/stores/${storeId}/admin/orders/${orderId}/items/${orderItemId}`
            );

            setOrders(prevOrders => {
                const tableOrders = prevOrders[tableId] || [];
                const updatedOrders = response.data.status === 'CANCELED'
                    ? tableOrders.filter(order => order.orderId !== orderId)
                    : tableOrders.map(order => order.orderId === orderId ? response.data : order);

                return { ...prevOrders, [tableId]: updatedOrders };
            });
        } catch {
            alert('주문 항목을 취소하지 못했습니다.');
        }
    };

    const handleSettleTable = async (tableId: number) => {
        if (!canSettleTable(tableId) || pendingTableIds.current.has(tableId)) return;
        pendingTableIds.current.add(tableId);
        setPendingTables(prev => ({ ...prev, [tableId]: true }));
        const settlingOrderIds = new Set(getActiveOrdersForTable(tableId).map(order => order.orderId));
        try {
            await axiosInstance.post(`/api/stores/${storeId}/admin/sales`, { tableId });
            setOrders(prev => ({ ...prev, [tableId]: (prev[tableId] || []).filter(order => !settlingOrderIds.has(order.orderId)) }));
            window.alert('정산이 완료되었습니다.');
        } catch {
            alert('정산 처리 중 오류가 발생했습니다.');
        } finally {
            pendingTableIds.current.delete(tableId);
            setPendingTables(prev => ({ ...prev, [tableId]: false }));
        }
    };

    const canSettleTable = (tableId: number) => !loadingOrders[tableId] && !orderErrors[tableId]
        && !pendingTables[tableId] && getActiveOrdersForTable(tableId).some(order => order.orderItems.length > 0);

    const canDeleteTable = (tableId: number) => !loadingOrders[tableId] && !orderErrors[tableId]
        && !pendingTables[tableId] && getActiveOrdersForTable(tableId).length === 0;

    const handleDeleteTable = async (tableId: number) => {
        const table = tables.find(item => item.id === tableId);
        if (!table) return;
        if (!canDeleteTable(table.id) || pendingTableIds.current.has(table.id)) return;
        if (!window.confirm(`'${table.tableName}' 테이블을 삭제할까요? 기존 QR코드는 사용할 수 없게 됩니다.`)) return;
        pendingTableIds.current.add(table.id);
        setPendingTables(prev => ({ ...prev, [table.id]: true }));
        try {
            await axiosInstance.delete(`/api/stores/${storeId}/admin/tables/${table.id}`);
            setTables(prev => prev.filter(item => item.id !== table.id));
            setSelectedReceiptTable(prev => prev?.id === table.id ? null : prev);
        } catch {
            alert('테이블을 삭제하지 못했습니다. 미정산 주문이 있는지 확인하고 다시 시도해 주세요.');
        } finally {
            pendingTableIds.current.delete(table.id);
            setPendingTables(prev => ({ ...prev, [table.id]: false }));
        }
    };

    if (isLoading) return <div style={containerStyle}>테이블 목록을 불러오는 중...</div>;
    if (error) return <div style={{ ...containerStyle, color: 'red' }}>오류: {error}</div>;

    const receiptOrders = selectedReceiptTable ? getActiveOrdersForTable(selectedReceiptTable.id) : [];
    const receiptRows = Array.from(
        receiptOrders
            .flatMap(order => order.orderItems)
            .reduce((acc, item) => {
                const key = `${item.productName}-${item.price}`;
                const current = acc.get(key) || {
                    productName: item.productName,
                    price: item.price,
                    quantity: 0,
                    total: 0,
                };

                current.quantity += item.quantity;
                current.total += item.price * item.quantity;
                acc.set(key, current);
                return acc;
            }, new Map<string, { productName: string; price: number; quantity: number; total: number }>())
            .values()
    );
    const receiptTotalPrice = receiptRows.reduce((sum, row) => sum + row.total, 0);
    const receiptTotalQuantity = receiptRows.reduce((sum, row) => sum + row.quantity, 0);

    return (
        <div style={pageContainerStyle}>
            {popup?.visible && (
                <div style={popupStyle}>
                    {popup.message}
                </div>
            )}
            {showTableInfo && (
                <TableInfoDialog tables={tables} storeId={storeId!} onClose={() => setShowTableInfo(false)}
                    onDeleteTable={handleDeleteTable} canDeleteTable={canDeleteTable} pendingTables={pendingTables} />
            )}
            {selectedReceiptTable && (
                <div style={modalBackdropStyle} onClick={() => setSelectedReceiptTable(null)}>
                    <div style={receiptModalStyle} onClick={(event) => event.stopPropagation()}>
                        <div style={qrModalHeaderStyle}>
                            <div>
                                <h3 style={{ margin: 0 }}>{selectedReceiptTable.tableName} 정산서</h3>
                                <p style={qrModalDescriptionStyle}>POS 입력용 주문 항목 집계입니다.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setSelectedReceiptTable(null)}
                                style={modalCloseButtonStyle}
                                aria-label="정산서 모달 닫기"
                            >
                                ×
                            </button>
                        </div>

                        <div style={receiptMetaStyle}>
                            <span>진행 주문 {receiptOrders.length}건</span>
                            <span>총 수량 {receiptTotalQuantity}개</span>
                        </div>

                        {receiptRows.length > 0 ? (
                            <>
                                <div style={receiptTableStyle}>
                                    <div style={receiptHeaderRowStyle}>
                                        <span>상품명</span>
                                        <span>단가</span>
                                        <span>수량</span>
                                        <span>금액</span>
                                    </div>
                                    {receiptRows.map(row => (
                                        <div key={`${row.productName}-${row.price}`} style={receiptRowStyle}>
                                            <strong>{row.productName}</strong>
                                            <span>{row.price.toLocaleString()}원</span>
                                            <span>{row.quantity}개</span>
                                            <strong>{row.total.toLocaleString()}원</strong>
                                        </div>
                                    ))}
                                </div>
                                <div style={receiptTotalStyle}>
                                    <span>합계</span>
                                    <strong>{receiptTotalPrice.toLocaleString()}원</strong>
                                </div>
                            </>
                        ) : (
                            <div style={emptyReceiptStyle}>정산할 진행 중 주문이 없습니다.</div>
                        )}
                    </div>
                </div>
            )}
            <div style={listContainerStyle}>
                <div style={headerStyle}>
                    <button onClick={handleGoBack} style={backButtonStyle}>←</button>
                    <h2 style={{ flexGrow: 1, margin: 0 }}>테이블 관리</h2>
                    <button
                        onClick={resumeAudio}
                        style={audioState === 'unsupported' ? soundButtonStyle : soundEnabledButtonStyle}
                        type="button"
                        disabled={audioState === 'unsupported'}
                        title={audioState === 'blocked' ? '화면을 한 번 클릭하면 알림음이 시작됩니다.' : '미확인 주문이 있으면 주문확인 전까지 반복해서 울립니다.'}
                    >
                        {audioState === 'unsupported' ? '알림음 지원 안 됨' : audioState === 'blocked' ? '알림음 켜짐 · 클릭하여 시작' : '알림음 켜짐'}
                    </button>
                    <button onClick={handleGoToQrPrintPage} style={qrPrintButtonStyle} type="button">
                        QR 인쇄
                    </button>
                    <button onClick={() => setShowTableInfo(true)} style={qrPrintButtonStyle} type="button" aria-haspopup="dialog">
                        테이블 정보
                    </button>
                    <button onClick={handleGoToCreatePage} style={createButtonStyle}>+ 새 테이블 생성</button>
                </div>
                <div style={tablesGridStyle}>
                    {tables.map((table) => (
                            <div
                                key={table.id}
                                className={(orders[table.id] || []).some(order => order.status === 'NEW') ? 'table-card--unconfirmed' : undefined}
                                style={{
                                    ...getCardStyle(table.isActive),
                                    backgroundColor: (orders[table.id] || []).some(order => order.status === 'NEW') ? '#fda4af' : '#fff',
                                    borderColor: table.isActive ? '#007bff' : '#ddd',
                                    cursor: 'default',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                }}
                            >
                                <div style={cardHeaderStyle}>
                                    <h3 style={{ margin: 0 }}>{table.tableName}</h3>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedReceiptTable(table)}
                                        style={receiptButtonStyle}
                                    >
                                        정산서
                                    </button>
                                </div>
                                <OrderHistorySidebar
                                    tableName={table.tableName}
                                    orders={
                                        getActiveOrdersForTable(table.id).filter(order => order.status !== 'SERVED')
                                    }
                                    isLoading={loadingOrders[table.id] || false}
                                    error={orderErrors[table.id] || null}
                                    onConfirmOrder={(orderId) => handleUpdateOrder(orderId, table.id, 'PROCESSING')}
                                    onServeOrder={(orderId) => handleUpdateOrder(orderId, table.id, 'SERVED')}
                                    onCancelOrderItem={(orderId, orderItemId) => handleCancelOrderItem(orderId, orderItemId, table.id)}
                                />
                                <button
                                    onClick={() => handleSettleTable(table.id)}
                                    disabled={!canSettleTable(table.id)}
                                    style={{ ...settleButtonStyle, backgroundColor: canSettleTable(table.id) ? '#28a745' : '#cbd5e1', cursor: canSettleTable(table.id) ? 'pointer' : 'not-allowed' }}
                                >
                                    {pendingTables[table.id] ? '처리 중...' : '정산하기'}
                                </button>
                            </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const listContainerStyle: React.CSSProperties = {
    minWidth: 0,
    overflowX: 'auto',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
};
const containerStyle: React.CSSProperties = { padding: '30px', fontFamily: 'sans-serif' };
const headerStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '15px',
    paddingBottom: '20px',
    marginBottom: '20px',
    borderBottom: '1px solid #eee',
};
const backButtonStyle: React.CSSProperties = {
    padding: '10px 15px',
    fontSize: '18px',
    lineHeight: 1,
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
};
const createButtonStyle: React.CSSProperties = {
    padding: '10px 20px',
    fontSize: '16px',
    cursor: 'pointer',
    color: 'white',
    backgroundColor: '#007bff',
    border: 'none',
    borderRadius: '5px',
};
const soundButtonStyle: React.CSSProperties = {
    padding: '10px 16px',
    fontSize: '16px',
    cursor: 'pointer',
    color: '#2563eb',
    backgroundColor: '#eef2ff',
    border: '1px solid #c7d2fe',
    borderRadius: '5px',
    fontWeight: 'bold',
};
const soundEnabledButtonStyle: React.CSSProperties = {
    ...soundButtonStyle,
    color: '#137333',
    backgroundColor: '#e8f7ee',
    border: '1px solid #b7e1c1',
};
const qrPrintButtonStyle: React.CSSProperties = {
    padding: '10px 16px',
    fontSize: '16px',
    cursor: 'pointer',
    color: '#334155',
    backgroundColor: '#f8fafc',
    border: '1px solid #cbd5e1',
    borderRadius: '5px',
    fontWeight: 'bold',
};
const cardHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
};
const receiptButtonStyle: React.CSSProperties = {
    minHeight: '32px',
    padding: '0 12px',
    border: '1px solid #16a34a',
    borderRadius: '6px',
    backgroundColor: '#f0fdf4',
    color: '#15803d',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 'bold',
};
const modalBackdropStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backgroundColor: 'rgba(15, 23, 42, 0.55)',
};
const receiptModalStyle: React.CSSProperties = {
    width: 'min(640px, 100%)',
    maxHeight: '86vh',
    overflowY: 'auto',
    padding: '22px',
    borderRadius: '8px',
    backgroundColor: '#fff',
    boxShadow: '0 20px 60px rgba(15, 23, 42, 0.3)',
    boxSizing: 'border-box',
};
const qrModalHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    alignItems: 'flex-start',
    marginBottom: '18px',
};
const qrModalDescriptionStyle: React.CSSProperties = {
    margin: '6px 0 0',
    color: '#64748b',
    fontSize: '14px',
};
const modalCloseButtonStyle: React.CSSProperties = {
    width: '32px',
    height: '32px',
    border: 'none',
    borderRadius: '50%',
    backgroundColor: '#f1f5f9',
    color: '#0f172a',
    cursor: 'pointer',
    fontSize: '22px',
    lineHeight: 1,
};
const receiptMetaStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '10px',
    marginBottom: '14px',
    padding: '10px 12px',
    borderRadius: '6px',
    backgroundColor: '#f8fafc',
    color: '#475569',
    fontWeight: 'bold',
};
const receiptTableStyle: React.CSSProperties = {
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    overflowX: 'auto',
};
const receiptHeaderRowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'minmax(120px, 1fr) 90px 70px 100px',
    gap: '8px',
    minWidth: '420px',
    padding: '10px 12px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    fontSize: '13px',
    fontWeight: 900,
};
const receiptRowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'minmax(120px, 1fr) 90px 70px 100px',
    gap: '8px',
    alignItems: 'center',
    minWidth: '420px',
    padding: '11px 12px',
    borderTop: '1px solid #e2e8f0',
    color: '#0f172a',
    fontSize: '14px',
};
const receiptTotalStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '14px',
    padding: '14px 16px',
    borderRadius: '8px',
    backgroundColor: '#ecfdf5',
    color: '#14532d',
    fontSize: '20px',
    fontWeight: 900,
};
const emptyReceiptStyle: React.CSSProperties = {
    padding: '28px',
    border: '1px dashed #cbd5e1',
    borderRadius: '8px',
    backgroundColor: '#f8fafc',
    color: '#64748b',
    textAlign: 'center',
    fontWeight: 'bold',
};
const pageContainerStyle: React.CSSProperties = {
    display: 'flex',
    padding: '0',
    gap: '10px',
    fontFamily: 'sans-serif',
    minHeight: '100vh',
    boxSizing: 'border-box',
    backgroundColor: '#f9f9f9',
};
const tablesGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(280px, 1fr))',
    gap: '10px',
    marginTop: '15px',
    overflowY: 'auto',
    alignContent: 'start',
    alignItems: 'start',
    width: '100%',
    boxSizing: 'border-box',
};
const popupStyle: React.CSSProperties = {
    position: 'fixed',
    top: 50,
    right: 50,
    zIndex: 9999,
    background: '#22c55e',
    color: 'white',
    padding: '18px 36px',
    borderRadius: 12,
    fontSize: 21,
    boxShadow: '0 8px 40px 0 rgba(0,0,0,0.16)',
    fontWeight: 'bold',
};
const settleButtonStyle: React.CSSProperties = {
    marginTop: 'auto',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    padding: '10px',
    cursor: 'pointer',
    fontWeight: 'bold',
};
const getCardStyle = (isActive: boolean): React.CSSProperties => ({
    border: `2px solid ${isActive ? '#007bff' : '#ddd'}`,
    borderRadius: '8px',
    padding: '10px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    boxSizing: 'border-box',
    width: '100%',
    aspectRatio: '1 / 1',
    overflow: 'hidden',
});

export default TableListPage;
